<script setup lang="ts">
import * as R from 'ramda'
import { Ref, ref, watch } from 'vue'

import { TeamSide } from '@collector/shared-domain'
import { Incident } from '@collector/sportsapi-client'
import { IncidentAttributeId } from '@collector/sportsapi-client-legacy'
import { useIncidentPlayers } from '@desktop/views/Relation/Sports/composables'
import { ShouldLoadOppositeSubParticipants } from '@desktop/views/Relation/Sports/configuration'
import { IncidentPlayerOption } from '@desktop/views/Relation/types'

import { getLoadOppositeParticipants } from '../utils'
import IncidentPlayerSelect from './IncidentPlayerSelect.vue'
import { useHistoryIncidentSubParticipantAvailability } from './useHistoryIncidentSubParticipantAvailability'
import { SubParticipantSettings } from './useSubParticipantAvailability'

interface Props {
  incident: Incident
  disabled: boolean
  teamSide: TeamSide | null
}

const props = defineProps<Props>()

// composables
const { homePlayersOptions, awayPlayersOptions } = useIncidentPlayers()
const {
  incidentParticipantSettings,
  incidentAssistantSettings,
  incidentSecondaryAssistantSettings,
  showParticipantSelect,
  showAssistantSelect,
  showSecondaryAssistantSelect,
} = useHistoryIncidentSubParticipantAvailability(props.incident)

// data
const players =
  props.teamSide === 'home' ? homePlayersOptions : awayPlayersOptions
const oppositePlayers =
  props.teamSide === 'home' ? awayPlayersOptions : homePlayersOptions

// state
const participantOptions: Ref<IncidentPlayerOption[]> = ref([])
const assistantOptions: Ref<IncidentPlayerOption[]> = ref([])
const secondaryAssistantOptions: Ref<IncidentPlayerOption[]> = ref([])

// methods
function getPlayersOptions(
  attributeIds: IncidentAttributeId[] | null,
  shouldLoadOppositeSubParticipants?: ShouldLoadOppositeSubParticipants,
): IncidentPlayerOption[] {
  return getLoadOppositeParticipants(
    attributeIds,
    shouldLoadOppositeSubParticipants,
  )
    ? oppositePlayers.value
    : players.value
}

function getFilteredPlayersOptions(
  attributeIds: IncidentAttributeId[] | null,
  settings: SubParticipantSettings,
): IncidentPlayerOption[] {
  return (
    settings?.filterSubParticipantsOptions?.(
      getPlayersOptions(
        attributeIds,
        settings.shouldLoadOppositeSubParticipants,
      ),
    ) ?? []
  )
}

function updateSubParticipantsOptions(
  attributeIds: IncidentAttributeId[] | null,
): void {
  participantOptions.value = getFilteredPlayersOptions(
    attributeIds,
    incidentParticipantSettings.value,
  )
  assistantOptions.value = getFilteredPlayersOptions(
    attributeIds,
    incidentAssistantSettings.value,
  )
  secondaryAssistantOptions.value = getFilteredPlayersOptions(
    attributeIds,
    incidentSecondaryAssistantSettings.value,
  )
}

// init
watch(
  () => props.incident.attribute_ids,
  (newVal, oldVal) => {
    if (
      !incidentParticipantSettings.value &&
      !incidentAssistantSettings.value &&
      !incidentSecondaryAssistantSettings.value
    ) {
      return
    }

    if (!R.equals(newVal, oldVal)) {
      updateSubParticipantsOptions(newVal)
    }
  },
  { immediate: true },
)

watch(
  () => [players.value, oppositePlayers.value],
  (newVal, oldVal) => {
    if (!R.equals(newVal, oldVal)) {
      updateSubParticipantsOptions(props.incident.attribute_ids)
    }
  },
)
</script>

<template>
  <IncidentPlayerSelect
    v-if="showParticipantSelect && incidentParticipantSettings"
    :label="incidentParticipantSettings.name"
    :required="incidentParticipantSettings.required"
    :type="incidentParticipantSettings.type"
    :keepSelectedOption="incidentParticipantSettings.keepSelectedOption"
    :incident
    :disabled
    :options="participantOptions"
    data-testid="scorer-select"
  />
  <IncidentPlayerSelect
    v-if="showAssistantSelect && incidentAssistantSettings"
    :label="incidentAssistantSettings.name"
    :required="incidentAssistantSettings.required"
    :type="incidentAssistantSettings.type"
    :keepSelectedOption="incidentAssistantSettings.keepSelectedOption"
    :incident
    :disabled
    :options="assistantOptions"
    data-testid="assistant-select"
  />
  <IncidentPlayerSelect
    v-if="showSecondaryAssistantSelect && incidentSecondaryAssistantSettings"
    :label="incidentSecondaryAssistantSettings.name"
    :required="incidentSecondaryAssistantSettings.required"
    :type="incidentSecondaryAssistantSettings.type"
    :keepSelectedOption="incidentSecondaryAssistantSettings.keepSelectedOption"
    :incident
    :disabled
    :options="secondaryAssistantOptions"
  />
</template>
